import styled from '@emotion/styled';

import type { TableColumnWidth } from '~/ui/core/table/Table';
import {
    TableBody as BaseTableBody,
    TableCell as BaseTableCell,
    TableContainer as BaseTableContainer,
    TableHeader as BaseTableHeader,
    TableHeaderCell as BaseTableHeaderCell,
    TableRow as BaseTableRow,
    TableGrid,
} from '~/ui/core/table/Table';
import { getConfigureColumnsTrackWidthWithOffset } from '~/ui/core/table/TableConfigureColumns';

import {
    ContentContainer as BaseContentContainer,
    Header as BaseHeader,
} from '../../../components/settings-page-styles';

export { Description, HeaderTop, Title, TitleGroup } from '../../../components/settings-page-styles';

export const ContentContainer = styled(BaseContentContainer)({
    overflow: 'unset',
});

export const Header = styled(BaseHeader)(({ theme }) => ({
    backgroundColor: 'var(--color-surface-raised)',
    paddingBottom: theme.spacing.s,
    position: 'sticky',
    top: 0,
    zIndex: 10,
}));

const COLUMN_IDS = ['checkbox', 'name', 'roles', 'status', 'authentication', 'lastSignedIn', 'actions'] as const;
type ColumnId = (typeof COLUMN_IDS)[number];

const COLUMN_WIDTHS: Readonly<Record<ColumnId, TableColumnWidth>> = {
    actions: '60px',
    authentication: 'minmax(100px, 0.6fr)',
    checkbox: '48px',
    lastSignedIn: 'minmax(120px, 0.7fr)',
    name: 'minmax(200px, 1.5fr)',
    roles: 'minmax(120px, 0.8fr)',
    status: 'minmax(80px, 0.5fr)',
};

export const USERS_AND_ROLES_CONFIGURE_COLUMNS_TRACK_WIDTH =
    getConfigureColumnsTrackWidthWithOffset('var(--spacing-s)');

const isColumnId = (columnId: string): columnId is ColumnId => columnId in COLUMN_WIDTHS;

export const getColumnWidth = (columnId: string): TableColumnWidth =>
    isColumnId(columnId) ? COLUMN_WIDTHS[columnId] : '1fr';

export const DEFAULT_GRID_COLUMN_WIDTHS: ReadonlyArray<TableColumnWidth> = [
    ...COLUMN_IDS.map((columnId) => COLUMN_WIDTHS[columnId]),
    USERS_AND_ROLES_CONFIGURE_COLUMNS_TRACK_WIDTH,
];

export const TableToolbar = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
    justifyContent: 'space-between',
    marginBottom: theme.spacing.l,
}));

export const SearchContainer = styled.div(({ theme }) => ({
    '& input': {
        border: 'none',
        flex: 1,
        outline: 'none',
        padding: 0,
    },
    alignItems: 'center',
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border-subtle)',
    borderRadius: theme.borderRadius.l,
    display: 'flex',
    gap: theme.spacing.s,
    maxWidth: '20rem',
    padding: `${theme.spacing.s} ${theme.spacing.l}`,
    width: '100%',
}));

export const SearchIcon = styled.div({
    alignItems: 'center',
    color: 'var(--color-text-disabled)',
    display: 'flex',
});

export const TableContainer = BaseTableContainer;

export const Grid = TableGrid;

export const GridHeader = styled(BaseTableHeader)(({ theme }) => ({
    padding: theme.spacing.s,
}));

export const GridHeaderCell = styled(BaseTableHeaderCell)(({ theme }) => ({
    '&[data-column="checkbox"]': {
        paddingLeft: theme.spacing.xs,
    },
}));

export const GridBody = styled(BaseTableBody)({
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
});

export const GridRow = styled(BaseTableRow)({
    '&:hover': {
        backgroundColor: 'var(--color-surface-softest)',
    },
    borderBottom: '1px solid var(--color-border-soft)',
});

export const GridCell = styled(BaseTableCell)(({ theme }) => ({
    '&[data-column="checkbox"]': {
        paddingLeft: theme.spacing.l,
    },
}));

export const UserCellContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
}));

export const NumberedAvatar = styled.div(({ theme }) => ({
    ...theme.typography.body.XS.semibold,
    alignItems: 'center',
    backgroundColor: 'var(--color-surface-subtle)',
    borderRadius: '50%',
    color: 'var(--color-text-content-subtle)',
    display: 'flex',
    flexShrink: 0,
    height: '2rem',
    justifyContent: 'center',
    width: '2rem',
}));

export const UserInfoCell = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
}));

export const UserName = styled.span(({ theme }) => ({
    ...theme.typography.body.S.medium,
    color: 'var(--color-text-secondary)',
    overflowWrap: 'anywhere',
}));

export const UserEmail = styled.span(({ theme }) => ({
    ...theme.typography.body.XS.regular,
    color: 'var(--color-text-content-faint)',
    overflowWrap: 'anywhere',
}));

export const DialogForm = styled.form({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
});

export const FormField = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
}));

export const FormLabel = styled.label(({ theme }) => ({
    ...theme.typography.body.S.semibold,
    color: 'var(--color-text-secondary)',
}));

export const DialogActions = styled.div(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing.m,
    justifyContent: 'flex-end',
    marginTop: theme.spacing.xl,
}));

export const UserInfoInDialog = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
    marginBottom: theme.spacing.xl,
}));

export const FormRow = styled.div(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing.l,
    gridTemplateColumns: '1fr 1fr',
}));
